import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Subscription } from 'rxjs';

import { Service } from '@shared/service/models';
import { ResourceSettings } from '@shared/restaurant/models';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';

@Component({
  selector: 'tablesready-add-edit-resource',
  templateUrl: './add-edit-resource.component.html',
  styleUrls: ['./add-edit-resource.component.scss']
})
export class AddEditResourceComponent implements OnInit, OnDestroy {
  @Input() resource;
  @Input() resourceCount;
  @Input() availableServices;
  @Input() categories;
  @Input() resourceSettings: ResourceSettings;

  icon = AppIcon;
  title: string;
  selectedServices: string[] = [];
  services: Service[];
  resourceForm: UntypedFormGroup;
  subscriptions = new Subscription();

  private readonly defaultCapacityValue: number = 1;

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly fb: UntypedFormBuilder,
  ) { }

  ngOnInit(): void {
    let isEditMode = false;
    this.title = 'Add';
    this.services = this.availableServices;

    if (this.resource && Object.keys(this.resource)?.length > 0) {
      this.title = 'Edit';
      isEditMode = true;
    }

    if (isEditMode) {
      this.selectedServices = this.services
        .filter(service => this.resource.services.includes(service._id))
        .map(service => service._id);
    } else {
      this.resource = {
        name: '',
        enabled: true,
        description: '',
        availability: {
          waitlist: true,
          reservations: true,
        },
        capacity: {
          min: this.defaultCapacityValue,
          max: this.defaultCapacityValue,
        },
        category: '',
        order: this.resourceCount,
      };

      if (this.resourceSettings.services.length > 0) {
        this.selectedServices = this.resourceSettings.services;
      }
    }

    this.resourceForm = this.fb.group({
      name: [this.resource.name, [Validators.required, Validators.maxLength(50)]],
      enabled: [this.resource.enabled],
      description: [this.resource.description || ''],
      capacity: this.fb.group({
        min: [this.resource.capacity.min, [Validators.required, Validators.min(0), Validators.max(this.resource.capacity.max)]],
        max: [this.resource.capacity.max, [Validators.required, Validators.min(1)]]
      }),
      availability: this.fb.group({
        waitlist: [this.resource.availability.waitlist],
        reservations: [this.resource.availability.reservations]
      }),
      services: [this.selectedServices || []],
      category: [this.resource.category]
    });

    const maxCapacitySub = this.capacityMaxControl.valueChanges.subscribe(max => {
      this.capacityMinControl.clearValidators();
      this.capacityMinControl.setValidators([Validators.required, Validators.min(0), Validators.max(max)]);
      this.capacityMinControl.updateValueAndValidity();
    });
    this.subscriptions.add(maxCapacitySub);
  }

  get nameControl(): AbstractControl {
    return this.resourceForm.get('name');
  }

  get enabledControl(): AbstractControl {
    return this.resourceForm.get('enabled');
  }

  get capacityMinControl(): AbstractControl {
    return this.resourceForm.get('capacity.min');
  }

  get capacityMaxControl(): AbstractControl {
    return this.resourceForm.get('capacity.max');
  }

  confirm(resource): void {
    if(this.resource._id) {
      resource._id = this.resource._id;
    }
    resource.category = resource.category || '';
    this.modalController.dismiss(resource);
  }

  cancel(): void {
    this.modalController.dismiss();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
